import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Combobox, Input, Radio } from '@node-space/ui-components'
import { FormFieldsRow } from 'components/Form/FormFieldsRow'
import { AddCustomerActions } from 'pages/EmbeddedCustomers/AddCustomer/AddCustomerActions'
import { BOOLEAN_RADIO_OPTIONS } from 'pages/EmbeddedCustomers/helpers/constants'
import { ADD_BUSINESS_CUSTOMER_V2_DEFAULT_VALUES } from '../helpers/constants'
import {
  type AddBusinessCustomerV2FormData,
  type ConfirmBusinessCustomerV2Data,
} from '../helpers/types'
import { useAddBusinessCustomerForm } from '../hooks/useAddBusinessCustomerForm'

export interface AddCustomerFormProps {
  defaultValues?: AddBusinessCustomerV2FormData
  onConfirm: (confirmData: ConfirmBusinessCustomerV2Data) => void
  onCancel: () => void
}

export const AddCustomerForm = ({ defaultValues, onConfirm, onCancel }: AddCustomerFormProps) => {
  const { t } = useTranslation()

  const {
    form,
    formValues,
    formFields,
    displayData,
    hasErrors,
    handleSubmit,
    setValue,
    showOperationalAddress,
  } = useAddBusinessCustomerForm(defaultValues ?? ADD_BUSINESS_CUSTOMER_V2_DEFAULT_VALUES)

  const onSubmit = (data: AddBusinessCustomerV2FormData) => {
    onConfirm({ data, displayData })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box flexCol gap={16}>
        <Input {...formFields.name} testId="v2-business-name" />
        <Combobox {...formFields.entityType} testId="v2-entity-type" />
        <Combobox {...formFields.useCase} testId="v2-use-case" />
        <Input {...formFields.registrationNumber} testId="v2-registration-number" />
        <FormFieldsRow
          leftField={<Input {...formFields.taxNumber} testId="v2-tax-number" />}
          rightField={
            <Combobox {...formFields.taxResidenceCountryCode} testId="v2-tax-residence-country" />
          }
        />
        <Input {...formFields.naicsCode} testId="v2-naics-code" />
        <Combobox {...formFields.monthlyExpectedVolumes} testId="v2-monthly-expected-volumes" />
        <Radio
          name="isRegulated"
          label={t('embeddedCustomers.businessForm.isRegulated')}
          options={BOOLEAN_RADIO_OPTIONS}
          value={formValues.isRegulated ?? ''}
          onChange={value => setValue('isRegulated', value, { shouldValidate: true })}
          error={form.formState.errors.isRegulated?.message}
          testId="v2-is-regulated"
        />
        <Combobox {...formFields.sourceOfFunds} testId="v2-source-of-funds" />
        <Combobox {...formFields.intendedUseOfAccount} testId="v2-intended-use-of-account" />

        <h3 className="text-h3 text-foreground-prominent pt-2">
          {t('customers.registeredAddress')}
        </h3>
        <Input {...formFields.registeredAddressLine1} testId="v2-registered-address-line1" />
        <FormFieldsRow
          leftField={
            <Input {...formFields.registeredAddressCity} testId="v2-registered-address-city" />
          }
          rightField={
            <Input
              {...formFields.registeredAddressPostalCode}
              testId="v2-registered-address-postal-code"
            />
          }
        />
        <Combobox
          {...formFields.registeredAddressCountryCode}
          testId="v2-registered-address-country"
        />

        <Radio
          name="isOperationalAddressDifferent"
          label={t('customers.isOperationalAddressDifferent')}
          options={BOOLEAN_RADIO_OPTIONS}
          value={formValues.isOperationalAddressDifferent ?? ''}
          onChange={value =>
            setValue('isOperationalAddressDifferent', value, { shouldValidate: true })
          }
          error={form.formState.errors.isOperationalAddressDifferent?.message}
          testId="v2-is-operational-address-different"
        />

        {showOperationalAddress && (
          <>
            <h3 className="text-h3 text-foreground-prominent pt-2">
              {t('customers.operationalAddress')}
            </h3>
            <Input {...formFields.operationalAddressLine1} testId="v2-operational-address-line1" />
            <FormFieldsRow
              leftField={
                <Input
                  {...formFields.operationalAddressCity}
                  testId="v2-operational-address-city"
                />
              }
              rightField={
                <Input
                  {...formFields.operationalAddressPostalCode}
                  testId="v2-operational-address-postal-code"
                />
              }
            />
            <Combobox
              {...formFields.operationalAddressCountryCode}
              testId="v2-operational-address-country"
            />
          </>
        )}
      </Box>
      <AddCustomerActions onCancel={onCancel} isDisableContinue={hasErrors} />
    </form>
  )
}
